import { bcs } from '@mysten/bcs';
import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
/** FungibleStakedSui.value is pool tokens, not DGC mist — convert via pool exchange rate. */
const FSS_DATA_KEY_TYPE = `${SUI_SYSTEM_ADDRESS}::staking_pool::FungibleStakedSuiDataKey`;
/**
 * Mirror of `staking_pool::calculate_fungible_staked_sui_withdraw_amount` rewards leg.
 * This is what `redeem_fungible_staked_sui_rewards_only` can withdraw (mist).
 */
export function calculateFungibleRewardsWithdrawMist(rate, fungibleStakedSuiValue, poolPrincipalMist, poolTotalSupply) {
    if (poolTotalSupply === 0n || fungibleStakedSuiValue === 0n)
        return 0n;
    const totalSuiAmount = poolTokensToCpcMist(poolTotalSupply, rate);
    const principalAmount = poolPrincipalMist < totalSuiAmount ? poolPrincipalMist : totalSuiAmount;
    const totalRewards = totalSuiAmount - principalAmount;
    return (fungibleStakedSuiValue * totalRewards) / poolTotalSupply;
}
export function poolTokensToCpcMist(poolTokenAmount, rate) {
    if (rate.poolTokenAmount === 0n || rate.suiAmount === 0n) {
        return poolTokenAmount;
    }
    return (rate.suiAmount * poolTokenAmount) / rate.poolTokenAmount;
}
function fungibleStakedSuiDataKeyBcs() {
    // On-chain key is `FungibleStakedSuiDataKey { dummy_field: bool }` (not empty struct).
    const key = bcs.struct('FungibleStakedSuiDataKey', {
        dummy_field: bcs.bool(),
    });
    return key.serialize({ dummy_field: false }).toBytes();
}
function tableInnerId(fields, key) {
    const node = fields[key];
    if (!node || typeof node !== 'object')
        return null;
    const inner = node.fields;
    return inner?.id?.id ?? null;
}
function parsePoolTokenExchangeRateBcs(bytes) {
    if (bytes.length < 16)
        return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return {
        suiAmount: view.getBigUint64(0, true),
        poolTokenAmount: view.getBigUint64(8, true),
    };
}
function parseExchangeRateFields(fields) {
    if (!fields)
        return null;
    const sui = fields.sui_amount ?? fields.suiAmount;
    const tok = fields.pool_token_amount ?? fields.poolTokenAmount;
    if (sui == null || tok == null)
        return null;
    return { suiAmount: BigInt(String(sui)), poolTokenAmount: BigInt(String(tok)) };
}
function parseRateFromDynamicFieldValue(value) {
    if (!value || typeof value !== 'object')
        return null;
    const v = value;
    if (v.bcs) {
        const bytes = typeof v.bcs === 'string'
            ? Uint8Array.from(atob(v.bcs), (c) => c.charCodeAt(0))
            : v.bcs;
        const fromBcs = parsePoolTokenExchangeRateBcs(bytes);
        if (fromBcs)
            return fromBcs;
    }
    const nested = v.value?.fields ?? v.fields;
    const direct = parseExchangeRateFields(nested);
    if (direct)
        return direct;
    return parseExchangeRateFields(v.fields);
}
export { fetchFungiblePoolData };
async function fetchRateFromTableDynamicField(client, tableId, epoch) {
    for (let e = epoch; e >= 0; e--) {
        try {
            if (client.getDynamicFieldObject) {
                const res = await client.getDynamicFieldObject({
                    parentId: tableId,
                    name: { type: 'u64', value: String(e) },
                });
                const rate = parseRateFromDynamicFieldValue(res.data?.content?.fields);
                if (rate)
                    return rate;
            }
            else if (client.getDynamicField) {
                const df = await client.getDynamicField({
                    parentId: tableId,
                    name: { type: 'u64', bcs: bcs.u64().serialize(e).toBytes() },
                });
                const rate = parseRateFromDynamicFieldValue(df.dynamicField?.value ?? df.data?.content?.fields);
                if (rate)
                    return rate;
                const fieldsRate = parseExchangeRateFields(df.data?.content?.fields);
                if (fieldsRate)
                    return fieldsRate;
            }
        }
        catch {
            continue;
        }
    }
    return null;
}
async function fetchRateFromValidatorExchangeTable(client, poolId, epoch) {
    if (!client.getLatestSuiSystemState)
        return null;
    const state = await client.getLatestSuiSystemState();
    const validators = state.activeValidators ?? [];
    const v = validators.find((x) => x.stakingPoolId === poolId);
    if (!v?.exchangeRatesId)
        return null;
    return fetchRateFromTableDynamicField(client, v.exchangeRatesId, epoch);
}
async function fetchPoolExchangeRateAtEpoch(client, poolId, epoch) {
    // Pool object is often not readable via getObject on local DGC nodes; validator table is reliable.
    const fromValidator = await fetchRateFromValidatorExchangeTable(client, poolId, epoch);
    if (fromValidator)
        return fromValidator;
    try {
        const poolRes = await client.getObject({ id: poolId, options: { showContent: true } });
        const poolFields = poolRes.data?.content?.fields;
        const tableId = poolFields ? tableInnerId(poolFields, 'exchange_rates') : null;
        if (tableId) {
            return fetchRateFromTableDynamicField(client, tableId, epoch);
        }
    }
    catch {
        /* pool object missing */
    }
    return null;
}
function u64Field(fields, key) {
    const v = fields?.[key];
    if (v == null)
        return null;
    return BigInt(String(v));
}
function parseFungiblePoolDataFields(fields) {
    if (!fields)
        return null;
    const principalMist = (() => {
        const principal = fields.principal;
        if (principal == null)
            return null;
        if (typeof principal === 'string' || typeof principal === 'number') {
            return BigInt(String(principal));
        }
        if (typeof principal !== 'object')
            return null;
        const p = principal;
        const v = p.fields?.value ?? p.value;
        return v == null ? null : BigInt(String(v));
    })();
    const lockUntilEpoch = u64Field(fields, 'lock_until_epoch');
    if (principalMist == null || lockUntilEpoch == null)
        return null;
    const totalSupply = u64Field(fields, 'total_supply') ?? 0n;
    const stakeActivationEpoch = u64Field(fields, 'stake_activation_epoch');
    return { principalMist, lockUntilEpoch, stakeActivationEpoch, totalSupply };
}
function poolDataFromFieldWrapper(fields) {
    if (!fields)
        return null;
    const valueNode = fields.value;
    if (valueNode?.fields) {
        return parseFungiblePoolDataFields(valueNode.fields);
    }
    return parseFungiblePoolDataFields(fields);
}
async function readPoolDataFromDynamicObjectField(client, parentId) {
    if (client.getDynamicFieldObject) {
        try {
            const res = await client.getDynamicFieldObject({
                parentId,
                name: { type: FSS_DATA_KEY_TYPE, value: { dummy_field: false } },
            });
            const d = poolDataFromFieldWrapper(res.data?.content?.fields);
            if (d != null)
                return d;
        }
        catch {
            /* */
        }
    }
    if (!client.getDynamicObjectField)
        return null;
    const keyBcs = fungibleStakedSuiDataKeyBcs();
    const { data } = await client.getDynamicObjectField({
        parentId,
        name: { type: FSS_DATA_KEY_TYPE, bcs: keyBcs },
    });
    return poolDataFromFieldWrapper(data?.content?.fields);
}
async function readPoolDataFromDynamicField(client, parentId) {
    if (client.getDynamicFieldObject) {
        try {
            const res = await client.getDynamicFieldObject({
                parentId,
                name: { type: FSS_DATA_KEY_TYPE, value: { dummy_field: false } },
            });
            const d = poolDataFromFieldWrapper(res.data?.content?.fields);
            if (d != null)
                return d;
        }
        catch {
            /* */
        }
    }
    if (!client.getDynamicField)
        return null;
    const keyBcs = fungibleStakedSuiDataKeyBcs();
    const df = await client.getDynamicField({
        parentId,
        name: { type: FSS_DATA_KEY_TYPE, bcs: keyBcs },
    });
    return poolDataFromFieldWrapper(df.dynamicField?.value?.fields ??
        df.data?.content?.fields);
}
async function readFssDataFromFieldObjectId(client, fieldObjectId) {
    const fieldRes = await client.getObject({
        id: fieldObjectId,
        options: { showContent: true },
    });
    const data = poolDataFromFieldWrapper(fieldRes.data?.content?.fields);
    return data != null && data.principalMist > 0n ? data : null;
}
async function findFssDataFieldObjectIdInTx(client, digest) {
    if (!client.getTransactionBlock)
        return null;
    const tx = await client.getTransactionBlock({
        digest,
        options: { showObjectChanges: true },
    });
    for (const ch of tx.objectChanges ?? []) {
        // After rewards_only the pool Field is mutated, not created (convert tx creates it).
        if (ch.objectId && ch.objectType?.includes('FungibleStakedSuiData')) {
            return ch.objectId;
        }
    }
    return null;
}
async function fetchFungiblePoolDataViaFssConvertTx(client, fungibleStakedSuiId) {
    if (!client.getTransactionBlock)
        return null;
    const fssRes = await client.getObject({
        id: fungibleStakedSuiId,
        options: { showContent: true, showPreviousTransaction: true },
    });
    let digest = fssRes.data?.previousTransaction;
    if (!digest)
        return null;
    for (let i = 0; i < 12 && digest; i++) {
        const fieldId = await findFssDataFieldObjectIdInTx(client, digest);
        if (fieldId) {
            const live = await readFssDataFromFieldObjectId(client, fieldId);
            if (live != null)
                return live;
        }
        try {
            const tx = await client.getTransactionBlock({
                digest,
                options: { showEffects: true },
            });
            digest = tx.effects?.previousTransaction;
        }
        catch {
            break;
        }
    }
    return null;
}
async function fetchFungiblePoolData(client, poolId, fungibleStakedSuiId) {
    try {
        const poolRes = await client.getObject({ id: poolId, options: { showContent: true } });
        const bagId = tableInnerId(poolRes.data?.content?.fields ?? {}, 'extra_fields');
        if (bagId) {
            const d = await readPoolDataFromDynamicObjectField(client, bagId);
            if (d != null)
                return d;
        }
    }
    catch {
        /* */
    }
    try {
        const d = await readPoolDataFromDynamicObjectField(client, poolId);
        if (d != null)
            return d;
    }
    catch {
        /* */
    }
    try {
        const d = await readPoolDataFromDynamicField(client, poolId);
        if (d != null)
            return d;
    }
    catch {
        /* suix_getDynamicFieldObject-style parent */
    }
    if (fungibleStakedSuiId) {
        return fetchFungiblePoolDataViaFssConvertTx(client, fungibleStakedSuiId);
    }
    return null;
}
export async function estimateFungibleStakedCpc(client, poolId, poolTokenValue, epoch, fungibleStakedSuiId) {
    const poolTokenAmount = BigInt(poolTokenValue);
    const [rate, poolData] = await Promise.all([
        fetchPoolExchangeRateAtEpoch(client, poolId, epoch),
        fetchFungiblePoolData(client, poolId, fungibleStakedSuiId),
    ]);
    const estimatedTotalCpcMist = rate
        ? poolTokensToCpcMist(poolTokenAmount, rate)
        : poolTokenAmount;
    const lockedPrincipalMist = poolData?.principalMist ?? null;
    const lockUntilEpoch = poolData?.lockUntilEpoch ?? null;
    const stakeActivationEpoch = poolData?.stakeActivationEpoch ?? null;
    let estimatedUnclaimedRewardMist = null;
    if (lockedPrincipalMist != null && rate && poolData && poolData.totalSupply > 0n) {
        estimatedUnclaimedRewardMist = calculateFungibleRewardsWithdrawMist(rate, poolTokenAmount, lockedPrincipalMist, poolData.totalSupply);
    }
    return {
        poolTokenAmount,
        estimatedTotalCpcMist,
        lockedPrincipalMist,
        lockUntilEpoch,
        stakeActivationEpoch,
        estimatedUnclaimedRewardMist,
    };
}
export { fetchPoolExchangeRateAtEpoch };
